import { useMemo } from 'react'
import { gql, useMutation, useQuery } from '@apollo/client'
import { useT } from '@transifex/react'
import { message } from 'src/antd'

import { BarcodeGenerationInput, LabelCodeType } from 'src/gen-types'

import {
  ContainerByPropertyGet,
  ContainerByPropertyGetVariables,
  LabelTicketsGet,
  LabelTicketsGetVariables,
  PrintContainerLabel,
  PrintContainerLabelVariables,
} from './__generated__/types'

const PRINT_CONTAINER_LABEL = gql`
  mutation PrintContainerLabel(
    $barcodeGenerationInputList: [BarcodeGenerationInput!]!
  ) {
    generateBarcodesForContainers(
      barcodeGenerationInputList: $barcodeGenerationInputList
    ) {
      linkToPdfFile
    }
  }
`

const GET_ALL_TICKETS_FOR_LABELS = gql`
  query LabelTicketsGet($ids: [ID]) {
    allTickets(ids: $ids) {
      edges {
        node {
          id
          container {
            id
            # Selecting only container.id defers container_type, which the
            # backend's select_related then conflicts on ("Field
            # Container.container_type cannot be both deferred and traversed
            # using select_related"), making the whole query error out so
            # ticketData is undefined and the button is disabled. Selecting
            # containerType keeps the resolver happy (PRO-669).
            containerType {
              id
            }
          }
          possibleContainerId
        }
      }
    }
  }
`

const GET_ALL_CONTAINERS_BY_PROPERTY = gql`
  query ContainerByPropertyGet($propertyIds: [ID]) {
    allContainers(propertyIds: $propertyIds) {
      edges {
        node {
          id
        }
      }
    }
  }
`

export type UsePrintContainerLabelArgs = {
  ticketIds?: string[]
  propertyIds?: string[]
}

type LabelTicketNode = {
  id?: string | null
  container?: { id?: string | null } | null
  possibleContainerId?: string | null
}

// A ticket can print a container label if it resolves an existing container
// (`container.id`) or a not-yet-created one at property level (a
// `possibleContainerId`, e.g. the "Tilføj ny beholder" tickets whose Beholder
// shows as `new_...`). Both are printable: the backend generates the label from
// the `ticket` id when no real `container.id` exists. The `possibleContainerId`
// (a synthetic `new_...` value) must NOT be sent as `containerId` — the mutation
// rejects it ("Something went wrong while generating label"); send '' instead
// and let the ticket id resolve it (PRO-669).
const isTicketPrintable = (node?: LabelTicketNode | null) =>
  Boolean(node?.container?.id || node?.possibleContainerId)

export type PrintLabelOptions = {
  // PRO-942: QR_CODE prints a QR code that carries the value the barcode
  // encodes. BARCODE is the default and is never sent.
  labelType?: LabelCodeType
}

export default function usePrintContainerLabel({
  ticketIds,
  propertyIds,
}: UsePrintContainerLabelArgs = {}) {
  const t = useT()

  const [printRequest, { loading }] = useMutation<
    PrintContainerLabel,
    PrintContainerLabelVariables
  >(PRINT_CONTAINER_LABEL)

  const { data: ticketData, loading: ticketLoading } = useQuery<
    LabelTicketsGet,
    LabelTicketsGetVariables
  >(GET_ALL_TICKETS_FOR_LABELS, {
    variables: {
      ids: ticketIds,
    },
    skip: !ticketIds?.length,
  })

  const { data: containerByPropertyData, loading: containerByPropertyLoading } =
    useQuery<ContainerByPropertyGet, ContainerByPropertyGetVariables>(
      GET_ALL_CONTAINERS_BY_PROPERTY,
      {
        variables: {
          propertyIds,
        },
        skip: !propertyIds?.length,
      }
    )

  const isAvailable = useMemo(() => {
    // more different ids can be added in future
    if (ticketIds) {
      // Enabled when every selected ticket is printable — i.e. resolves either a
      // real container or a property-level (`new_...`) container. Both print, so
      // nothing is silently skipped; a genuinely container-less ticket in the
      // selection correctly disables the action (PRO-669).
      const edges = ticketData?.allTickets?.edges
      return !ticketLoading && !!edges?.length
        ? edges.every((ticket) => isTicketPrintable(ticket?.node))
        : false
    }

    if (propertyIds) {
      return (
        !containerByPropertyLoading &&
        !!containerByPropertyData?.allContainers?.edges?.length
      )
    }

    return true
  }, [
    propertyIds,
    ticketIds,
    containerByPropertyData,
    ticketData,
    containerByPropertyLoading,
    ticketLoading,
  ])

  const printLabel = (
    ids?: BarcodeGenerationInput[],
    { labelType }: PrintLabelOptions = {}
  ) => {
    let queryIds: BarcodeGenerationInput[] = []

    if (ticketIds) {
      // Send the real container id when there is one, otherwise '' so the
      // backend resolves the (property-level `new_...`) container from the
      // ticket id. Never send possibleContainerId as containerId — the mutation
      // rejects the synthetic `new_...` value.
      queryIds =
        ticketData?.allTickets?.edges?.map((ticket) => ({
          containerId: ticket?.node?.container?.id || '',
          ticket: ticket?.node?.id || '',
        })) || []
    }

    if (propertyIds) {
      queryIds =
        containerByPropertyData?.allContainers?.edges?.map((container) => ({
          containerId: container?.node?.id || '',
        })) || []
    }

    const items = ids || queryIds
    // PRO-939: only a non-default label type travels, on every item. The
    // barcode never names the field, so its request is the one it always was
    // and keeps printing against a backend that predates labelType.
    const barcodeGenerationInputList =
      labelType && labelType !== LabelCodeType.BARCODE
        ? items.map((item) => ({ ...item, labelType }))
        : items
    return printRequest({
      variables: { barcodeGenerationInputList },
    })
      .then((resp) => {
        message.success(t('Print label request sent successfully'))
        const fileLink =
          resp?.data?.generateBarcodesForContainers?.linkToPdfFile
        if (fileLink) {
          window.open(fileLink, '_blank')
        }
        return false
      })
      .catch(() => {
        message.error(t('Something went wrong'))
      })
  }

  return {
    loading,
    disabled: !isAvailable,
    printLabel,
  }
}

export type PrintLabel = ReturnType<typeof usePrintContainerLabel>['printLabel']
